/**
 * Same-origin helpers for top-level Entra handoffs from the CDF preview iframe
 * (or any other embedded panel). Interactive Entra pages cannot render in a
 * generic iframe — open a top-level tab instead.
 */

export const AUTH_ACTION_PARAM = 'authAction';
export const RETURN_TO_PARAM = 'returnTo';

/** Graph consent actions request only the permission needed by the user action. */
export type AuthAction = 'login' | 'logout' | 'acquireToken' | 'acquireGraphCalendar' | 'acquireGraphPlaces' | 'acquireGraphPresence' | 'acquireGraphDirectory' | 'acquireGraphManagerReports' | 'acquireGraphGroups';

const AUTH_ACTIONS = new Set<AuthAction>([
  'login',
  'logout',
  'acquireToken',
  'acquireGraphCalendar',
  'acquireGraphPlaces',
  'acquireGraphPresence',
  'acquireGraphDirectory',
  'acquireGraphManagerReports',
  'acquireGraphGroups'
]);

export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Accept only relative same-origin app paths. Reject protocol-relative / absolute URLs. */
export function isSafeReturnTo(path: string): boolean {
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('://')) return false;
  if (path.includes('\\')) return false;
  return true;
}

function currentReturnTo(): string {
  const combined = `${window.location.pathname}${window.location.search}`;
  return isSafeReturnTo(combined) ? combined : '/';
}

/** Build a top-level URL that carries a single-use auth intent. */
export function buildTopLevelAuthUrl(action: AuthAction, returnTo?: string): string {
  const url = new URL(window.location.origin + '/');
  url.searchParams.set(AUTH_ACTION_PARAM, action);

  const candidate = returnTo ?? currentReturnTo();
  if (isSafeReturnTo(candidate)) {
    url.searchParams.set(RETURN_TO_PARAM, candidate);
  }

  url.hash = '';
  return url.toString();
}

/**
 * Read and remove a single-use auth intent from the URL before any interaction
 * starts. Idempotent for subsequent Strict Mode remounts (param is already gone).
 */
export function consumeAuthIntent(): { action: AuthAction; returnTo: string | null } | null {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(AUTH_ACTION_PARAM);
  if (!raw || !AUTH_ACTIONS.has(raw as AuthAction)) return null;

  const action = raw as AuthAction;
  const returnRaw = url.searchParams.get(RETURN_TO_PARAM);
  const returnTo = returnRaw && isSafeReturnTo(returnRaw) ? returnRaw : null;

  url.searchParams.delete(AUTH_ACTION_PARAM);
  url.searchParams.delete(RETURN_TO_PARAM);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, next);

  return { action, returnTo };
}

/**
 * Opens a top-level tab for Entra interaction. Must remain synchronous to the
 * user click stack so popup policies allow it.
 */
export function openTopLevelAuth(action: AuthAction, returnTo?: string): boolean {
  const opened = window.open(buildTopLevelAuthUrl(action, returnTo), '_blank', 'noopener,noreferrer');
  if (!opened) {
    console.warn(
      'Popup blocked: allow popups for this site, or open the preview URL in a full browser tab to sign in.'
    );
    return false;
  }
  return true;
}
